import { useCallback } from 'react';
import type { IHighlight } from 'react-pdf-highlighter';
import { types } from 'react-rough-notation';
import type { Cuepoint, MessageOrigin } from '../types';

type Brackets = 'left' | 'right' | 'top' | 'bottom';

interface UseSourceHandlersParams {
  sourceHighlightColor: string;
  documentIds: string[];
  setHighlights: React.Dispatch<React.SetStateAction<IHighlight[]>>;
  setCuepoints: React.Dispatch<React.SetStateAction<Cuepoint[]>>;
  pdfViewerRef: React.MutableRefObject<unknown>;
}

export interface UseSourceHandlersResult {
  prepareSource: (source: MessageOrigin) => {
    highlight: IHighlight;
    cuepoint: Cuepoint;
  };
  showIndividualSources: (params: {
    highlightsSources: IHighlight[];
    cuepointsSources: Cuepoint[];
  }) => void;
  handleDisplaySource: (source: MessageOrigin) => void;
}

export const useSourceHandlers = ({
  sourceHighlightColor,
  documentIds,
  setHighlights,
  setCuepoints,
  pdfViewerRef,
}: UseSourceHandlersParams): UseSourceHandlersResult => {
  const prepareSource = useCallback(
    (source: MessageOrigin) => {
      const id = 'source-' + String(Date.now());
      const boundingRect = source?.zone?.position?.bounding_rect;
      const rects = source?.zone?.position?.rects;
      const quote = source?.zone?.quote;
      const pageNumber = source?.zone?.position?.page_number;
      const highlightFromCoord = {
        content: {
          text: quote,
          image: '-',
        },
        position: {
          usePdfCoordinates: true,
          boundingRect,
          rects: rects,
          pageNumber: pageNumber,
        },
        comment: {
          text: '',
          emoji: '',
        },
        id: id,
      };
      const cuepointFromCoord = {
        title: '',
        source: 'source',
        roughNotation: {
          animate: false,
          type: 'highlight' as types,
          animationDelay: 500,
          animationDuration: 0,
          strokeWidth: 0,
          padding: 0,
          color: sourceHighlightColor,
          brackets: ['left'] as Brackets[],
          iterations: 0,
          multiline: true,
          show: true,
        },
        start: 0,
        highlightId: id,
        documentId: documentIds[0],
        page: 0,
        top: 0,
      };
      return {
        highlight: highlightFromCoord as IHighlight,
        cuepoint: cuepointFromCoord as Cuepoint,
      };
    },
    [sourceHighlightColor, documentIds],
  );

  const showIndividualSources = useCallback(
    ({
      highlightsSources,
      cuepointsSources,
    }: {
      highlightsSources: IHighlight[];
      cuepointsSources: Cuepoint[];
    }) => {
      setHighlights([highlightsSources[0]]);
      setCuepoints([cuepointsSources[0]]);
      // Function call on pdfViewerRef
      (
        pdfViewerRef?.current as
          | { scrollViewerTo: (highlight: IHighlight) => void }
          | undefined
      )?.scrollViewerTo(highlightsSources[0]);
    },
    [setHighlights, setCuepoints, pdfViewerRef],
  );

  const handleDisplaySource = useCallback(
    (source: MessageOrigin) => {
      const sources = {
        highlightsSources: [] as IHighlight[],
        cuepointsSources: [] as Cuepoint[],
      };
      const { highlight, cuepoint } = prepareSource(source);
      sources.highlightsSources.push(highlight);
      sources.cuepointsSources.push(cuepoint);
      showIndividualSources(sources);
    },
    [prepareSource, showIndividualSources],
  );

  return {
    prepareSource,
    showIndividualSources,
    handleDisplaySource,
  };
};
